import { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import CoursePlaceholder from "../../../assets/CoursePlaceholder.png";
import { Course } from "../../../types/types";
import QuizXIcon from "../../Icons/QuizXIcon";
import { DeleteCourseFromStudent } from "../../../api/Students/DeleteCourseFromStudent";
import { queryClient } from "../../../main";
import ReactMarkdown from "react-markdown";
import { LockKeyhole, Play } from "lucide-react";

const CourseCard = ({ course, studentId }: { course: Course; studentId?: string; }) => {
    const location = useLocation();
    const isMyProfile = location.pathname === "/my-profile";
    const [showFullDescription] = useState(false);
    const descriptionWords = course.description.split(' ');
    const truncatedDescription = showFullDescription ? descriptionWords.join(' ') : descriptionWords.slice(0, 22).join(' ');
    const { mutate: deleteCourseFromStudent } = useMutation({
        mutationFn: DeleteCourseFromStudent,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["students", "courses"] });
            toast.success("Course deleted from student successfully");
        },
    });
    studentId = studentId || '';
    const courseDetailsPath = `/course/${course.slug}`;
    const resumePath = course.lastLectureId
        ? `/lecture/${course.id}/${course.lastLectureId}${
            course.lastVideoId
                ? `?video=${encodeURIComponent(course.lastVideoId)}`
                : ""
          }`
        : courseDetailsPath;
    const isFrozen = course.frozen === true;

    return (
        <div className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-black/10 bg-white p-4 shadow-sm transition duration-300 hover:-translate-y-0.5 hover:shadow-[0_22px_55px_rgba(17,24,22,0.12)]">
            <div className="relative mb-4">
                <Link to={courseDetailsPath}>
                    <img
                        className="aspect-[4/3] w-full rounded-xl border border-black/5 object-cover transition duration-300 group-hover:scale-[1.015]"
                        src={course.img_url || CoursePlaceholder}
                        alt="Course"
                    />
                </Link>
            </div>
            {!isMyProfile && (
                <button   onClick={() => {
                    if (studentId) {
                      deleteCourseFromStudent({ studentId, courseId: course.id });
                    } else {
                      console.error("Student ID is undefined.");
                    }
                  }}
                
                    className="absolute bottom-3 right-3 flex h-8 w-8 items-center justify-center rounded-xl bg-primary text-white shadow-lg">
                    <QuizXIcon />
                </button>
            )}
            <div className="flex justify-between w-full items-center mb-3 py-0.5">
                <h1 className="text-lg font-bold text-textPrimary sm:text-xl md:text-2xl">{course?.name}</h1>
            </div>
            <div className="mb-4">
                <span className="text-sm font-medium text-lightGray">
                <ReactMarkdown>{truncatedDescription}</ReactMarkdown>
                </span>
                {!showFullDescription && (
                    <Link className="text-sm font-bold text-primary" to={`/course/${course.slug}`}>&nbsp; Learn more about {course.name}</Link>
                )}
            </div>
            {isMyProfile && (
                <div className="mt-auto pt-2">
                    {isFrozen && (
                        <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-[#8a5a1f]">
                            <LockKeyhole size={14} />
                            This course is currently frozen
                        </p>
                    )}
                    <Link
                        to={resumePath}
                        aria-disabled={isFrozen}
                        onClick={(event) => {
                            if (isFrozen) event.preventDefault();
                        }}
                        className={`flex h-11 w-full items-center justify-center gap-2 rounded-xl px-4 text-sm font-bold text-white transition ${
                            isFrozen
                                ? "cursor-not-allowed bg-[#9aa5aa] opacity-75"
                                : "bg-primary shadow-[0_8px_20px_rgba(12,48,82,0.16)] hover:bg-secondary"
                        }`}
                    >
                        <Play size={16} fill="currentColor" />
                        Continue learning
                    </Link>
                </div>
            )}
        </div>
    );
};

export default CourseCard;
